import React from 'react'
import { IMeetupPlace } from '../../static/interfaces'

export interface IMeetupPlaceOption {
  value: string
  label: string
}

export interface IMeetupPlaceFilterOption {
  value: string | number
}

interface IMeetupPlaceSearchCache {
  sub_area: string
  name_ja: string
  name_en: string
}

const generateMeetupPlaceLabel = ({ name_en, name_ja }: IMeetupPlace): string => {
  return `${name_en} | ${name_ja}`
}

export const useMeetupPlaceOptions = (meetupPlaces: IMeetupPlace[]) => {
  const meetupPlaceOptions = React.useMemo<IMeetupPlaceOption[]>(() => {
    return [
      {
        value: '',
        label: ' - ',
      },
      ...meetupPlaces.map(p => ({ value: String(p.id), label: generateMeetupPlaceLabel(p) })),
    ]
  }, [meetupPlaces])

  const meetupPlaceCacheMap = React.useMemo<Map<number | null, IMeetupPlaceSearchCache>>(() => {
    const cacheMap = new Map<number | null, IMeetupPlaceSearchCache>()
    meetupPlaces.forEach(place => {
      cacheMap.set(place.id, {
        sub_area: place.sub_area?.toLowerCase() || '',
        name_ja: place.name_ja?.toLowerCase() || '',
        name_en: place.name_en?.toLowerCase() || '',
      })
    })
    return cacheMap
  }, [meetupPlaces])

  const customFilterOption = React.useCallback(
    (option: IMeetupPlaceFilterOption, searchText: string): boolean => {
      if (!searchText) {
        return true
      }

      const targetPlace = meetupPlaceCacheMap.get(Number(option.value))
      if (!targetPlace) {
        return false
      }

      const separated = searchText
        .toLowerCase()
        .split(/[\s　]+/)
        .filter(k => k.trim() !== '')

      return separated.every(query => {
        return (
          targetPlace.sub_area.includes(query) ||
          targetPlace.name_ja.includes(query) ||
          targetPlace.name_en.includes(query)
        )
      })
    },
    [meetupPlaceCacheMap]
  )

  return { meetupPlaceOptions, customFilterOption }
}
